import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { Base } from '@adornis/buildify/db/Base.js';
import { BaseGeneralEditDocumentationExtension } from '@chancenwerk/_buildify/edit-ui/entities/BaseGeneralEditDocumentationExtension.js';
import { BaseGeneralStudentDocumentationExtension } from '@chancenwerk/_buildify/edit-ui/entities/BaseGeneralStudentDocumentationExtension.js';
import { BaseKPFEditAccessCodesExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditAccessCodesExtension.js';
import { BaseKPFEditDefaultExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditDefaultExtension.js';
import { BaseKPFEditFinancialExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditFinancialExtension.js';
import { BaseKPFEditPartnerExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditPartnerExtension.js';
import { BaseKPFEditPersonsExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditPersonsExtension.js';
import { BaseKPFEditProductExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditProductExtension.js';
import { BaseKPFEditRAGReportingExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-kpf/BaseKPFEditRAGReportingExtension.js';
import { BasePartnerEditAcquisitionExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditAcquisitionExtension.js';
import { BasePartnerEditContactPersonExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditContactPersonExtension.js';
import { BasePartnerEditDefaultExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditDefaultExtension.js';
import { BasePartnerEditDurationExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditDurationExtension.js';
import { BasePartnerEditEvaluationExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditEvaluationExtension.js';
import { BasePartnerEditExternalFundingExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditExternalFundingExtension.js';
import { BasePartnerEditMaterial } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditMaterial.js';
import { BasePartnerEditMediaExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditMediaExtension.js';
import { BasePartnerEditSchoolformExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerEditSchoolformExtension.js';
import { BasePartnerLinkKPFsExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerLinkKPFsExtension.js';
import { BasePartnerViewSubDivisionsExtension } from '@chancenwerk/_buildify/edit-ui/entities/edit-partner/BasePartnerViewSubDivisionsExtension.js';
import { ChancenPersonalConfig } from '@chancenwerk/user-configs/entities/ChancenPersonalConfig.js';

/**
 * @entity EditUIConfig
 */
@Entity()
export class EditUIPageConfig extends AdornisEntity {
  public static override _class = 'EditUIPageConfig';

  @Field(type => String, {
    default: v => v ?? A.getGloballyUniqueID(),
  })
  public _id: Maybe<string>;

  @Field(type => String, {
    default: v => v ?? 'Unbenannt',
  })
  public name: Maybe<string>;

  @Field(type => [Base], {
    default: v => v ?? [],
  })
  public uiSchema: Maybe<Base[]>;
}

/**
 * @entity EditUIConfig
 */
@Entity()
export class EditUIConfig extends ChancenPersonalConfig {
  public static override _class = 'EditUIConfig';

  @Field(type => [EditUIPageConfig], {
    default: v => v ?? [],
  })
  public pages: Maybe<EditUIPageConfig[]>;

  public static override getDefault(category: string, key: string) {
    switch (category) {
      case 'edit-partner':
        return new EditUIConfig({
          category,
          key,
          pages: [
            new EditUIPageConfig({
              name: 'allgemein',
              uiSchema: [
                new BasePartnerEditDefaultExtension({
                  open: true,
                }),
                new BasePartnerViewSubDivisionsExtension({}),
                new BasePartnerEditSchoolformExtension({
                  open: true,
                }),
                new BasePartnerEditMaterial({}),
                new BasePartnerEditMediaExtension({}),
              ],
            }),
            new EditUIPageConfig({
              name: 'Zusammenarbeit & Akquise',
              uiSchema: [
                new BasePartnerEditDurationExtension({
                  open: true,
                }),
                new BasePartnerEditAcquisitionExtension({}),
                new BasePartnerEditEvaluationExtension({}),
              ],
            }),
            new EditUIPageConfig({
              name: 'Produkte (KPF)',
              uiSchema: [
                new BasePartnerLinkKPFsExtension({
                  open: true,
                }),
              ],
            }),
            new EditUIPageConfig({
              name: 'Netzwerk/Förderung',
              uiSchema: [
                new BasePartnerEditContactPersonExtension({
                  open: true,
                }),
                new BasePartnerEditExternalFundingExtension({}),
              ],
            }),
            new EditUIPageConfig({
              name: 'Dokumentationen',
              uiSchema: [
                new BaseGeneralEditDocumentationExtension({
                  open: true,
                }),
              ],
            }),
          ],
        });
      case 'edit-kpf-without-rag':
        return new EditUIConfig({
          category,
          key,
          pages: [
            new EditUIPageConfig({
              name: 'allgemein',
              uiSchema: [
                new BaseKPFEditPartnerExtension({ open: true }),
                new BaseKPFEditProductExtension({ open: true }),
                new BaseKPFEditDefaultExtension({}),
                new BaseKPFEditAccessCodesExtension({}),
              ],
            }),
            new EditUIPageConfig({
              name: 'Finanzierung & Förderung',
              uiSchema: [new BaseKPFEditFinancialExtension({ open: true })],
            }),
            new EditUIPageConfig({
              name: 'Ansprechpersonen',
              uiSchema: [new BaseKPFEditPersonsExtension({ open: true })],
            }),
            new EditUIPageConfig({
              name: 'Dokumentationen',
              uiSchema: [
                new BaseGeneralEditDocumentationExtension({
                  open: true,
                }),
                new BaseGeneralStudentDocumentationExtension({
                  open: true,
                }),
              ],
            }),
          ],
        });
      case 'edit-kpf':
        return new EditUIConfig({
          category,
          key,
          pages: [
            new EditUIPageConfig({
              name: 'allgemein',
              uiSchema: [
                new BaseKPFEditPartnerExtension({ open: true }),
                new BaseKPFEditProductExtension({ open: true }),
                new BaseKPFEditDefaultExtension({}),
                new BaseKPFEditAccessCodesExtension({}),
              ],
            }),
            new EditUIPageConfig({
              name: 'Finanzierung & Förderung',
              uiSchema: [new BaseKPFEditFinancialExtension({ open: true })],
            }),
            new EditUIPageConfig({
              name: 'Ansprechpersonen',
              uiSchema: [new BaseKPFEditPersonsExtension({ open: true })],
            }),
            new EditUIPageConfig({
              name: 'Dokumentationen',
              uiSchema: [
                new BaseGeneralEditDocumentationExtension({
                  open: true,
                }),
                new BaseGeneralStudentDocumentationExtension({
                  open: true,
                }),
              ],
            }),
            new EditUIPageConfig({
              name: 'RAG-Reporting',
              uiSchema: [
                new BaseKPFEditRAGReportingExtension({
                  open: true,
                }),
              ],
            }),
          ],
        });
      case 'create-kpf':
        return new EditUIConfig({
          category,
          key,
          pages: [
            new EditUIPageConfig({
              name: 'allgemein',
              uiSchema: [
                new BaseKPFEditPartnerExtension({ open: true }),
                new BaseKPFEditProductExtension({ open: true }),
                new BaseKPFEditDefaultExtension({}),
                new BaseKPFEditAccessCodesExtension({}),
              ],
            }),
          ],
        });
      default:
        throw new Error('Tried to get default. But key ' + category + ' has no default');
    }
  }
}
